/**
 * Profile Tile Styles
 *
 * Shared tile/card styling that varies by UI profile.
 * Every page that renders navigation tiles or module cards should use
 * getProfileTileStyles(profile) to get consistent styling.
 *
 * USAGE:
 *   import { useProfileTileStyle } from '@/components/ProfileProvider'
 *   const tileStyle = useProfileTileStyle()
 *   <div style={{ ...tileStyle.container, padding: '1.5rem' }}>
 *     <h3 style={tileStyle.title}>Title</h3>
 *     <p style={tileStyle.desc}>Description</p>
 *   </div>
 */

import { type UIProfile } from './ui-profiles'

export interface ProfileTileStyle {
  /** Container/card wrapper styles */
  container: React.CSSProperties
  /** Hover override styles (applied onMouseEnter) */
  containerHover: React.CSSProperties
  /** Title text styles */
  title: React.CSSProperties
  /** Description text styles */
  desc: React.CSSProperties
  /** Accent badge styles (e.g., "ADMIN ONLY" tag) */
  badge: React.CSSProperties
}

const CLASSIC_TILE: ProfileTileStyle = {
  container: {
    background: 'var(--t-surface)',
    border: '1px solid var(--t-border)',
    borderRadius: '16px',
    transition: 'border-color 0.2s, background 0.2s',
  },
  containerHover: {
    borderColor: 'var(--t-border-hover)',
  },
  title: {
    fontWeight: 700,
    fontSize: '1rem',
    color: 'var(--t-text)',
  },
  desc: {
    color: 'var(--t-text-4)',
    fontSize: '0.8rem',
    lineHeight: 1.5,
  },
  badge: {
    background: 'var(--t-accent-dim)',
    color: 'var(--t-accent)',
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.6rem',
    fontWeight: 700,
  },
}

const SCIFI_TILE: ProfileTileStyle = {
  container: {
    background: 'rgba(250, 204, 21, 0.03)',
    border: '1px solid rgba(250, 204, 21, 0.15)',
    borderRadius: '16px',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 0 20px rgba(250, 204, 21, 0.05), inset 0 1px 0 rgba(250, 204, 21, 0.08)',
    transition: 'border-color 0.3s, box-shadow 0.3s',
  },
  containerHover: {
    borderColor: 'rgba(250, 204, 21, 0.4)',
    boxShadow: '0 0 30px rgba(250, 204, 21, 0.12), inset 0 1px 0 rgba(250, 204, 21, 0.15)',
  },
  title: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#facc15',
    fontFamily: '"Courier New", Courier, monospace',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
  desc: {
    color: 'var(--t-text-4)',
    fontSize: '0.78rem',
    lineHeight: 1.5,
    fontFamily: '"Courier New", Courier, monospace',
  },
  badge: {
    background: 'rgba(250, 204, 21, 0.1)',
    color: '#facc15',
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.6rem',
    fontWeight: 700,
    border: '1px solid rgba(250, 204, 21, 0.2)',
  },
}

const BASEBALL_TILE: ProfileTileStyle = {
  container: {
    background: 'linear-gradient(160deg, #1a1a3e 0%, #0d0d2b 60%, #1a0a0a 100%)',
    border: '2px solid rgba(245, 158, 11, 0.3)',
    borderRadius: '14px',
    boxShadow: '0 4px 20px rgba(245, 158, 11, 0.08)',
    transition: 'border-color 0.3s, box-shadow 0.3s, transform 0.2s',
  },
  containerHover: {
    borderColor: 'rgba(245, 158, 11, 0.6)',
    boxShadow: '0 6px 30px rgba(245, 158, 11, 0.15)',
    transform: 'translateY(-2px)',
  },
  title: {
    fontWeight: 800,
    fontSize: '1rem',
    color: '#f59e0b',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  desc: {
    color: 'var(--t-text-4)',
    fontSize: '0.8rem',
    lineHeight: 1.5,
  },
  badge: {
    background: 'rgba(245, 158, 11, 0.15)',
    color: '#f59e0b',
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.6rem',
    fontWeight: 700,
    border: '1px solid rgba(245, 158, 11, 0.25)',
  },
}

const PROFILE_TILE_STYLES: Record<UIProfile, ProfileTileStyle> = {
  classic: CLASSIC_TILE,
  scifi: SCIFI_TILE,
  baseball: BASEBALL_TILE,
}

/**
 * Get tile styles for a given profile.
 * Pure function — safe to call in any context (server or client).
 */
export function getProfileTileStyles(profile: UIProfile): ProfileTileStyle {
  return PROFILE_TILE_STYLES[profile] ?? CLASSIC_TILE
}
